import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Landing.css';

const SIGN_IN = '/signin';

const LOOP_STEPS = [
  { label: 'Connect', detail: 'Bring ShipHero, Shopify, Amazon, QuickBooks, Klaviyo, and Meta into one operating view.' },
  { label: 'Reconcile', detail: 'Surface mismatches, low-stock risks, and changes before they become surprises.' },
  { label: 'Cost', detail: 'Make landed cost, weighted cost, and COGS canonical across every channel.' },
  { label: 'Plan', detail: 'Set demand basis, explode bundles, and model Open-to-Buy with the real constraints.' },
  { label: 'Decide', detail: 'Turn the numbers into a clear, permissioned recommendation your team can review.' },
  { label: 'Execute', detail: 'Move from approved purchase orders to receiving, payment schedules, and inventory.' },
  { label: 'Audit', detail: 'Keep the change log, approval trail, and source context attached to every decision.' },
];

const PILLARS = [
  {
    title: 'Grounded in your own systems',
    body: 'Eadin is the operating layer across the systems you already use. The answer starts with your catalog, orders, costs, cash, and history—not a disconnected spreadsheet.',
  },
  {
    title: 'Costing you can defend',
    body: 'Landed and weighted cost give finance, planning, and operations the same COGS view. See how the number was built, then trace it back to its source.',
  },
  {
    title: 'Permissioned by role',
    body: 'A shared source of truth does not mean shared access to everything. RBAC keeps each team in the right view, with approvals before consequential writes.',
  },
];

const COMPARISON = [
  { task: 'Knowing what is actually in stock', generic: 'Each channel and dashboard tells a different story.', eadin: 'Reconciles supply, orders, receiving, and channel data into one view.' },
  { task: 'Planning the next buy', generic: 'A spreadsheet copy with stale demand and missing constraints.', eadin: 'Uses trailing, YoY, or forecast demand with MOQ, carton, and bundle logic.' },
  { task: 'Understanding margin', generic: 'Revenue minus an assumed product cost.', eadin: 'A canonical COGS view built from landed and weighted cost.' },
  { task: 'Changing the operation safely', generic: 'A cell changed, but nobody knows why.', eadin: 'Role-aware approvals, change logs, and an audit trail on every change.' },
];

function CTA({ children, variant = 'primary' }) {
  return (
    <Link className={`mkt-cta mkt-cta-${variant}`} to={SIGN_IN}>
      {children}
    </Link>
  );
}

function SystemNode({ children }) {
  return <span className="mkt-system-node">{children}</span>;
}

export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  return (
    <main className="mkt">
      <header className="mkt-nav">
        <Link className="mkt-brand" to="/" aria-label="Eadin — Enterprise Intelligence">
          <img className="mkt-brand-logo" src="/brand/eadin-logo-full.png" alt="Eadin — Enterprise Intelligence" height={34} />
        </Link>
        <nav className="mkt-nav-menu" aria-label="Main navigation">
          <div className="mkt-nav-item">
            <button className="mkt-nav-trigger" type="button">Platform <span className="mkt-caret" aria-hidden="true">▾</span></button>
            <div className="mkt-nav-dropdown">
              <a className="mkt-nav-drop-link" href="#systems">Connected systems</a>
              <a className="mkt-nav-drop-link" href="#how-it-works">How it works</a>
              <a className="mkt-nav-drop-link" href="#trust">Governance</a>
            </div>
          </div>
          <div className="mkt-nav-item">
            <button className="mkt-nav-trigger" type="button">Solutions <span className="mkt-caret" aria-hidden="true">▾</span></button>
            <div className="mkt-nav-dropdown">
              <a className="mkt-nav-drop-link" href="#planning">Demand planning</a>
              <a className="mkt-nav-drop-link" href="#finance">Finance &amp; cost</a>
              <a className="mkt-nav-drop-link" href="#operations">Operations</a>
            </div>
          </div>
        </nav>
        <nav className="mkt-nav-actions" aria-label="Account navigation">
          <Link className="mkt-nav-link" to={SIGN_IN}>Log in</Link>
          <Link className="mkt-nav-cta" to={SIGN_IN}>Get started</Link>
        </nav>
        <button className="mkt-nav-toggle" type="button" aria-label="Menu" aria-expanded={menuOpen} onClick={() => setMenuOpen((value) => !value)}>
          <span aria-hidden="true" /><span aria-hidden="true" /><span aria-hidden="true" />
        </button>
      </header>

      {menuOpen && (
        <div className="mkt-mobile-menu">
          <a className="mkt-mobile-link" href="#systems" onClick={closeMenu}>Connected systems</a>
          <a className="mkt-mobile-link" href="#how-it-works" onClick={closeMenu}>How it works</a>
          <a className="mkt-mobile-link" href="#planning" onClick={closeMenu}>Demand planning</a>
          <a className="mkt-mobile-link" href="#finance" onClick={closeMenu}>Finance &amp; cost</a>
          <Link className="mkt-mobile-link" to={SIGN_IN} onClick={closeMenu}>Log in</Link>
          <Link className="mkt-mobile-cta" to={SIGN_IN} onClick={closeMenu}>Get started</Link>
        </div>
      )}

      <section className="mkt-hero">
        <div className="mkt-hero-copy">
          <img
            className="mkt-hero-logo"
            src="/brand/eadin-logo-full.png"
            alt="Eadin — Enterprise Intelligence"
          />
          <h1 className="mkt-h1">Planning, inventory, purchasing, finance, forecasting, and operations—all connected.</h1>
          <p className="mkt-lede">Eadin connects the systems behind your commerce business so every team can plan, buy, move, and report with numbers they can trust.</p>
          <div className="mkt-cta-row mkt-cta-row-center">
            <CTA>Request access →</CTA>
            <CTA variant="ghost">Log in</CTA>
          </div>
          <ul className="mkt-badges mkt-badges-center">
            <li>One view across every channel</li>
            <li>Costing tied to real sources</li>
            <li>Human approval before writes</li>
          </ul>
        </div>
      </section>

      <section className="mkt-showcase" aria-hidden="true">
        <div className="mkt-showcase-grid">
          <article className="mkt-card mkt-rec">
            <div className="mkt-card-head"><span className="mkt-card-dot" /><span className="mkt-card-kind">Illustrative · Demand planning</span></div>
            <p className="mkt-rec-label">Reorder recommendation</p>
            <p className="mkt-rec-text">Bring <strong>1,200 units</strong> of the Trail Pack into the next PO.</p>
            <p className="mkt-rec-label">Why this is the right buy</p>
            <ul className="mkt-rec-list">
              <li><strong>6.4 weeks</strong> of coverage at trailing demand</li>
              <li>Rounded to vendor <strong>MOQ and carton</strong> constraints</li>
              <li>Bundle demand included in the component requirement</li>
            </ul>
          </article>
          <article className="mkt-card mkt-rec">
            <div className="mkt-card-head"><span className="mkt-card-dot" /><span className="mkt-card-kind">Illustrative · Container optimization</span></div>
            <p className="mkt-rec-label">Consolidation decision</p>
            <p className="mkt-rec-text">Consolidate the next shipment to protect availability without overbuying.</p>
            <p className="mkt-rec-label">Expected operating view</p>
            <ul className="mkt-rec-list">
              <li>Freight plan reflects <strong>landed cost</strong>, not unit cost alone</li>
              <li>Projected <strong>in-stock probability</strong> stays visible</li>
              <li>Payment schedule and AP impact travel with the PO</li>
            </ul>
          </article>
        </div>
      </section>

      <section className="mkt-source" id="systems">
        <div className="mkt-section-head">
          <p className="mkt-eyebrow">Single source of truth</p>
          <h2 className="mkt-h2">The systems stay specialized. The decision gets unified.</h2>
          <p className="mkt-section-sub">Eadin brings the operational context together without asking your team to replace the tools that run the business.</p>
        </div>
        <div className="mkt-flow" aria-label="Connected systems flow">
          <div className="mkt-system-group">
            <SystemNode>ShipHero</SystemNode><SystemNode>Shopify</SystemNode><SystemNode>Amazon</SystemNode>
            <SystemNode>QuickBooks</SystemNode><SystemNode>Klaviyo</SystemNode><SystemNode>Meta</SystemNode>
          </div>
          <div className="mkt-flow-line" aria-hidden="true">→</div>
          <div className="mkt-eadin-node"><img src="/brand/eadin-mark.png" alt="" />Eadin</div>
          <div className="mkt-flow-line" aria-hidden="true">→</div>
          <div className="mkt-decision-node"><span>Decisions</span><small>plan · buy · report · act</small></div>
        </div>
        <div className="mkt-kpi-grid">
          <div><strong>Landed cost</strong><span>Canonical COGS view</span></div>
          <div><strong>Demand basis</strong><span>Trailing · YoY · forecast</span></div>
          <div><strong>Open-to-Buy</strong><span>Capital meets coverage</span></div>
          <div><strong>Audit trail</strong><span>Every change has context</span></div>
        </div>
      </section>

      <section className="mkt-band mkt-agitate">
        <h2 className="mkt-h2">Your spreadsheets are multiplying.<br /><em>Your certainty is not.</em></h2>
        <p className="mkt-band-lede">Procurement has one number. Finance has another. The warehouse is working from a third. Eadin gives every team the same operational ground truth—and the context behind it.</p>
      </section>

      <section className="mkt-section" id="operations">
        <div className="mkt-section-head">
          <p className="mkt-eyebrow">Point tools vs Eadin</p>
          <h2 className="mkt-h2">Stop reconciling the business by hand.</h2>
          <p className="mkt-section-sub">The difference is not another dashboard. It is a connected operating model that can explain its numbers.</p>
        </div>
        <div className="mkt-compare">
          <div className="mkt-compare-row mkt-compare-headrow"><span className="mkt-compare-task" /><span className="mkt-compare-cell mkt-compare-generic-head">Spreadsheets + point tools</span><span className="mkt-compare-cell mkt-compare-kip-head">Eadin</span></div>
          {COMPARISON.map((row) => <div className="mkt-compare-row" key={row.task}><span className="mkt-compare-task">{row.task}</span><span className="mkt-compare-cell mkt-compare-generic">{row.generic}</span><span className="mkt-compare-cell mkt-compare-kip">{row.eadin}</span></div>)}
        </div>
      </section>

      <section className="mkt-section" id="how-it-works">
        <div className="mkt-section-head">
          <p className="mkt-eyebrow">How it works</p>
          <h2 className="mkt-h2">One operating loop. Fewer blind spots.</h2>
          <p className="mkt-section-sub">Connect the business, make the numbers canonical, and carry the decision all the way through execution.</p>
        </div>
        <ol className="mkt-loop">
          {LOOP_STEPS.map((step, index) => <li className="mkt-loop-step" key={step.label}><span className="mkt-loop-num">{index + 1}</span><div><p className="mkt-loop-label">{step.label}</p><p className="mkt-loop-detail">{step.detail}</p></div></li>)}
        </ol>
      </section>

      <section className="mkt-section mkt-pillars-section" id="planning">
        <div className="mkt-section-head"><p className="mkt-eyebrow">The Eadin standard</p><h2 className="mkt-h2">Intelligence that earns its place in the operation.</h2></div>
        <div className="mkt-pillars">{PILLARS.map((pillar) => <article className="mkt-pillar" key={pillar.title}><h3 className="mkt-pillar-title">{pillar.title}</h3><p className="mkt-pillar-body">{pillar.body}</p></article>)}</div>
      </section>

      <section className="mkt-band mkt-multiply">
        <p className="mkt-eyebrow">Compounding operational leverage</p>
        <h2 className="mkt-h2">A small operations team can run a large catalog.<br /><em>When every decision starts from the same truth.</em></h2>
        <p className="mkt-band-lede">Eadin turns the work that used to live in handoffs, re-keyed exports, and fragile models into a repeatable operating system. More context for every operator. Less time spent making the numbers agree.</p>
      </section>

      <section className="mkt-section mkt-aha-section" id="finance">
        <div className="mkt-section-head"><p className="mkt-eyebrow">The better answer</p><h2 className="mkt-h2">What’s our true margin?</h2><p className="mkt-section-sub">The question is simple. The answer should not depend on which spreadsheet someone opened last.</p></div>
        <div className="mkt-aha">
          <div className="mkt-aha-col"><p className="mkt-aha-tag">Spreadsheet answer</p><p className="mkt-aha-q">“Revenue minus the product cost we have on file.”</p><p className="mkt-aha-a">A snapshot with unclear assumptions, missing freight, and no shared trail back to the source.</p></div>
          <div className="mkt-aha-arrow" aria-hidden="true">→</div>
          <div className="mkt-aha-col mkt-aha-col-smart"><p className="mkt-aha-tag mkt-aha-tag-smart">Eadin answer</p><p className="mkt-aha-q">“Here is the margin by channel, using canonical landed cost.”</p><p className="mkt-aha-a">A finance-ready answer with weighted cost, channel context, and an auditable path to every input.</p></div>
        </div>
      </section>

      <section className="mkt-band mkt-trust" id="trust">
        <h2 className="mkt-h2 mkt-trust-h2">Built for accountable operations.</h2>
        <ul className="mkt-trust-list">
          <li><strong>RBAC and least privilege.</strong> People see and change what their role allows.</li>
          <li><strong>Full audit trail.</strong> Changes carry their source, actor, and context.</li>
          <li><strong>Tenant isolation.</strong> Your operation remains your operation.</li>
          <li><strong>Human approval before writes.</strong> Recommendations do not become actions without a person.</li>
        </ul>
      </section>

      <section className="mkt-final">
        <img className="mkt-final-mark" src="/brand/eadin-mark.png" alt="" />
        <h2 className="mkt-h2 mkt-final-h2">Make every operating decision<br />start from the same truth.</h2>
        <div className="mkt-cta-row mkt-cta-row-center"><CTA>Request access →</CTA><CTA variant="ghost">Log in</CTA></div>
      </section>

      <footer className="mkt-footer"><span>Eadin — Enterprise Intelligence</span><span className="mkt-footer-dot">·</span><Link className="mkt-footer-link" to={SIGN_IN}>Log in</Link></footer>
    </main>
  );
}
